import { Color, Spacing } from '@persona-id/design-tokens';
import { CircleAlertIcon } from '@persona-id/design-tokens/icons';
import styled from 'styled-components';

import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import TableLayout, { TableIconTD, TableNullState } from 'dashboard/layouts/TableLayout';
import { ReportStatus } from 'dashboard/models/report';
import { ReportBusinessIndustryClassification } from 'dashboard/models/report/business-industry-classification';
import Card from 'design-system/components/Card/Card';
import Sticker from 'design-system/components/Sticker';
import Table, {
  TableBody,
  TableCell,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { Paragraph } from 'design-system/components/Text';

const StickerGroup = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing[1]};
  max-width: 400px;
`;

export interface BusinessIndustryClassificationProps {
  report: ReportBusinessIndustryClassification;
  isRedacted: boolean;
  multiCardFormat: boolean;
}

const BusinessIndustryClassification = ({
  report,
  isRedacted,
  multiCardFormat,
}: BusinessIndustryClassificationProps) => {
  if (report.attributes.status === ReportStatus.Submitted) {
    return (
      <ReportResultLayout multiCardFormat={false}>
        <Card>
          <Card.Body>
            The report is still running, please refresh this page after a few minutes to see the
            results.
          </Card.Body>
        </Card>
      </ReportResultLayout>
    );
  }
  const industryClassificationCategories =
    report.attributes.result?.['industry-classification']?.categories || [];
  const industryClassificationRows = industryClassificationCategories.map((category) => {
    return {
      'industry-name': category.name,
      'naics-codes': category['naics-codes'],
      'is-high-risk': category['high-risk'],
    };
  });

  return isRedacted ? (
    <ReportResultLayout multiCardFormat={multiCardFormat}>
      <div>
        <Sticker>Redacted</Sticker>
      </div>
    </ReportResultLayout>
  ) : (
    <ReportResultLayout multiCardFormat={multiCardFormat}>
      <Card>
        <Card.Title>Industry Classifications</Card.Title>
        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
        <IndustryClassificationTable rows={industryClassificationRows} />
      </Card>
    </ReportResultLayout>
  );
};

interface IndustryClassificationRow {
  'industry-name': string;
  'naics-codes': string[];
  'is-high-risk': boolean;
}

interface IndustryClassificationTableProps {
  rows: IndustryClassificationRow[];
}

const IndustryClassificationTable = ({ rows }: IndustryClassificationTableProps) => {
  return (
    <Table>
      <TableLayout
        isEmpty={rows.length === 0}
        showHeader={true}
        nullState={<TableNullState>Unable to classify business.</TableNullState>}
        headerTR={
          <>
            <TableHeaderCell></TableHeaderCell>
            <TableHeaderCell>Industry Name</TableHeaderCell>
            <TableHeaderCell>NAICS Codes</TableHeaderCell>
          </>
        }
      >
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={row['industry-name']}>
              <TableIconTD>
                {row['is-high-risk'] && <CircleAlertIcon color={Color.CoreRed700} />}
              </TableIconTD>
              <TableCell>{row['industry-name']}</TableCell>
              <TableCell>
                {row['naics-codes'].length > 0 ? (
                  <StickerGroup>
                    {row['naics-codes'].map((code) => (
                      <Sticker key={`industry-classification-row-sticker-${i}__${code}`}>
                        {code}
                      </Sticker>
                    ))}
                  </StickerGroup>
                ) : (
                  <Paragraph color='tertiary'>N/A</Paragraph>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </TableLayout>
    </Table>
  );
};

export default BusinessIndustryClassification;
